import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';

/** 화면 표시용 — KoshaPortal JSON (Authorization·Cookie 줄은 JSX에서 빨간 강조) */
const KOSHA_PORTAL_CONFIG_HEAD = `{
  "KoshaPortal": {
    "BaseUrl": "https://portal.kosha.or.kr",
    "SyncDetailAfterLibrary": true,
    "SyncPrepareBeforeLibrary": false,
    "SelectKrasPrparePath": "/api/portal24/bizC/CRSBA01002/selectKrasPrpare",
    "SelectPostIaTpbizPath": "/api/portal24/bizC/CRSBA01002/selectPostIaTpbiz",
    "SelectIaTpbizIndustryListPath": "/api/portal24/bizC/CRSBA01002/selectIndustryPopList",
    "SelectPostIaTpbizRiskDetailPath": "/api/portal24/bizC/CRSBA01003/selectRiskFactor",
    "SelectRsknssLevelDcsPath": "",
    "SelectRiskLevelPath": "/api/portal24/bizC/CRSBA01004/selectRiskLevel",
    "SelectHealthMeasuresPopListPath": "/api/portal24/bizC/CRSBA01004/selectHealthMeasuresPopList",
    "RiskLevelRowsPerPage": 100,
    "HealthMeasuresRowsPerPage": 100,
    "RefererPrepare": "https://portal.kosha.or.kr/kras/implement/real/prepare",
    "RefererHealthMeasures": "https://portal.kosha.or.kr/kras/implement/real/sub3",
    "SyncHealthMeasuresAfterDetail": true,
    "SyncHealthMeasuresAfterRiskLevel": true,
`;

const KOSHA_PORTAL_CONFIG_REFERER_LINE = `    "Referer": "https://portal.kosha.or.kr/kras/implement/real/sub2",
`;

const KOSHA_PORTAL_CONFIG_TAIL = `  }
}`;

const koshaPortalHighlightSx = {
  color: 'error.main',
  fontWeight: 700,
  display: 'block',
};

/** 공정 목록 응답 예시 — 응답 본문에 dataList 배열로 내려옴 */
const KOSHA_PROCESS_DATALIST_EXAMPLE = `"dataList": [
  {
    "iaTpbizJobProcsCd": "T00001",
    "iaTpbizSclsfCd": "00001",
    "iaTpbizSclsfDtlCd": null,
    "rsknsEvlJobProcsNm": "고객방문",
    "iaTpbizJobProcsExpln": "은행업무를 보기위한 고객 응대",
    "iaTpbizJobProcsMchnExpln": "ATM기, 순번 대기표 출력기",
    "iaTpbizJobProcsNxsExpln": null,
    "iaTpbizJobProcsSeq": 1
  },
  {
    "iaTpbizJobProcsCd": "T00002",
    "iaTpbizSclsfCd": "00001",
    "iaTpbizSclsfDtlCd": null,
    "rsknsEvlJobProcsNm": "고객상담",
    "iaTpbizJobProcsExpln": "은행업무를 보기위한 고객 상담",
    "iaTpbizJobProcsMchnExpln": null,
    "iaTpbizJobProcsNxsExpln": null,
    "iaTpbizJobProcsSeq": 2
  },
  {
    "iaTpbizJobProcsCd": "T00003",
    "iaTpbizSclsfCd": "00001",
    "iaTpbizSclsfDtlCd": null,
    "rsknsEvlJobProcsNm": "고객 은행 업무(입금,출금,대출 등)",
    "iaTpbizJobProcsExpln": "고객의 자금을 입금 및 출금, 대출상담 업무",
    "iaTpbizJobProcsMchnExpln": "지폐계수기",
    "iaTpbizJobProcsNxsExpln": null,
    "iaTpbizJobProcsSeq": 3
  },
  {
    "iaTpbizJobProcsCd": "T00004",
    "iaTpbizSclsfCd": "00001",
    "iaTpbizSclsfDtlCd": null,
    "rsknsEvlJobProcsNm": "기타사무업무",
    "iaTpbizJobProcsExpln": "은행업무와 관련된 사무를 보는 업무",
    "iaTpbizJobProcsMchnExpln": "복사기, 컴퓨터",
    "iaTpbizJobProcsNxsExpln": null,
    "iaTpbizJobProcsSeq": 4
  }
]`;

/** 위험요인 상세 응답 예시 — 조문 전문(sttLprvsCn 등)은 길어 화면용으로 축약 */
const KOSHA_RISK_FACTOR_DATALIST_EXAMPLE = `"dataList": [
  {
    "stdNxsRiskFctrNo": 21180,
    "iaTpbizJobProcsCd": "T00001",
    "riskDtlClsfCd": "1.1",
    "nxsRiskFctrCn": "위험발생 상황 및 결과",
    "sttSeNm": "산업안전보건법",
    "sttLprvsTtlNm": "산업안전보건법 제 42조(유해위험방지계획서의 작성ㆍ제출 등)",
    "sttLprvsCn": "제42조 … (조문 본문 전체가 문자열로 내려옴, 생략)",
    "riskDtlClsfNm": "협착위험 부분(감김,끼임)",
    "addActnCn": null
  },
  {
    "stdNxsRiskFctrNo": 67,
    "iaTpbizJobProcsCd": "T00001",
    "riskDtlClsfCd": "1.2",
    "nxsRiskFctrCn": "칼 등의 수공구에 의한 베임",
    "sttSeNm": "산업안전보건기준에 관한 규칙",
    "sttLprvsTtlNm": "산업안전보건기준에 관한 규칙 제 32조(보호구의 지급 등)",
    "sttLprvsCn": "제32조 … (생략)",
    "riskDtlClsfNm": "위험한 표면(절단, 베임, 긁힘)",
    "addActnCn": "1. 작업시 베임 방지용 안전장갑 착용\\r\\n2. 안전보건교육 실시"
  },
  {
    "stdNxsRiskFctrNo": 68,
    "iaTpbizJobProcsCd": "T00001",
    "riskDtlClsfCd": "6.3",
    "nxsRiskFctrCn": "작업장 바닥의 이물질로 인한 넘어짐 위험",
    "sttLprvsTtlNm": "산업안전보건기준에 관한 규칙 제 3조(전도의 방지)",
    "riskDtlClsfNm": "공간 및 이동통로",
    "addActnCn": "1. 작업장 정리정돈 및 청소\\r\\n2. 전도예방조치(고정)"
  }
]`;

/** 업종선택 팝업 응답 예시 — 업종 분류/표준산업 분류가 dataList에 내려옴 */
const KOSHA_INDUSTRY_DATALIST_EXAMPLE = `"dataList": [
  {
    "iaTpbizSclsfCd": "00001",
    "tpbizLclsfNm": "금융및보험업",
    "tpbizMclsfNm": "금융및보험업",
    "iaTpbizSclsfCdNm": "금융업",
    "iaTpbizSclsfDtlCd": "P00001",
    "iaTpbizSclsfDtlCdNm": "은행원 업무",
    "stdIndutyLclasNm": "금융 및 보험업",
    "stdIndutyMlsfcNm": "금융업",
    "stdIndutyNm": "은행 및 저축기관",
    "srchKywdNm": null
  },
  {
    "iaTpbizSclsfCd": "00002",
    "tpbizLclsfNm": "금융및보험업",
    "tpbizMclsfNm": "금융및보험업",
    "iaTpbizSclsfCdNm": "보험및연금업",
    "iaTpbizSclsfDtlCd": "P00002",
    "iaTpbizSclsfDtlCdNm": "보증보험업부",
    "stdIndutyLclasNm": "금융 및 보험업",
    "stdIndutyMlsfcNm": "보험업",
    "stdIndutyNm": "보험업",
    "srchKywdNm": null
  },
  {
    "iaTpbizSclsfCd": "00003",
    "tpbizLclsfNm": "금융및보험업",
    "tpbizMclsfNm": "금융및보험업",
    "iaTpbizSclsfCdNm": "금융및보험관련서비스업",
    "iaTpbizSclsfDtlCd": "P00003",
    "iaTpbizSclsfDtlCdNm": "생명보험대리점",
    "stdIndutyLclasNm": "금융 및 보험업",
    "stdIndutyMlsfcNm": "금융 및 보험관련 서비스업",
    "stdIndutyNm": "금융 지원 서비스업",
    "srchKywdNm": null
  },
  {
    "iaTpbizSclsfCd": "10001",
    "tpbizLclsfNm": "광  업",
    "tpbizMclsfNm": "석탄광업및채석업",
    "iaTpbizSclsfCdNm": "무연탄광업",
    "iaTpbizSclsfDtlCd": "P00004",
    "iaTpbizSclsfDtlCdNm": "무연탄 채굴/채취업",
    "stdIndutyLclasNm": "광업",
    "stdIndutyMlsfcNm": "석탄, 원유 및 천연가스 광업",
    "stdIndutyNm": "석탄 광업",
    "srchKywdNm": null
  },
  {
    "iaTpbizSclsfCd": "10002",
    "tpbizLclsfNm": "광  업",
    "tpbizMclsfNm": "석탄광업및채석업",
    "iaTpbizSclsfCdNm": "갈탄광업",
    "iaTpbizSclsfDtlCd": "P00005",
    "iaTpbizSclsfDtlCdNm": "갈탄 채굴/채취업",
    "stdIndutyLclasNm": "광업",
    "stdIndutyMlsfcNm": "석탄, 원유 및 천연가스 광업",
    "stdIndutyNm": "석탄 광업",
    "srchKywdNm": null
  }
]`;

const codePreSx = {
  m: 0,
  p: 2,
  borderRadius: 1,
  bgcolor: 'grey.100',
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'auto',
  maxHeight: 360,
  fontSize: '0.8rem',
  lineHeight: 1.45,
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
};

function KOSHA_TEMP_01(props) {
  const {
    syncDetailAllLoading,
    syncAllRiskLibraryOnlyLoading,
    fetchIndustryLoading,
    FetchKoshaIndustryCodes,
    SyncAllRiskDetail,
    SyncAllRiskLibraryOnly,
  } = props;

  const busy =
    syncDetailAllLoading ||
    syncAllRiskLibraryOnlyLoading ||
    fetchIndustryLoading;

  return (
    <Box sx={{ p: 3 }}>
      {/* 1. 업종 목록 조회 (FetchKoshaIndustryCodes) */}
      <Card
        variant="outlined"
        sx={{ borderLeft: '4px solid', borderColor: 'primary.main' }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            1. 업종정보코드(산재업종-대중소/표준산업-대중소) 조회 및 업종 테이블
            등록
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            공단의<code> selectIndustryPopList</code> API를 호출하여
            업종정보코드(산재업종-대중소/표준산업-대중소)를 조회합니다.
            <br />
            <strong>TB_BS_INDUSTRY_CODE</strong> 테이블에 INSERT만 합니다.
            (중복체크없음.)
          </Typography>

          <Button
            variant="outlined"
            color="secondary"
            onClick={FetchKoshaIndustryCodes}
            disabled={busy}
          >
            {fetchIndustryLoading
              ? '업종 목록 조회 중...'
              : '업종 목록 조회 및 업종 테이블 등록 (FetchKoshaIndustryCodes)'}
          </Button>

          {fetchIndustryLoading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="indeterminate" color="secondary" />
            </Box>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      {/* 2. 업종별 공정(작업) 동기화 (POST) */}
      <Card
        variant="outlined"
        sx={{ borderLeft: '4px solid', borderColor: 'warning.main' }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            2. 업종별 공정(작업) 동기화
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <code>TB_BS_INDUSTRY_CODE</code> 테이블에 있는 업종을 모두 순회하며
            공단의
            <code> selectPostIaTpbiz</code> API를 호출하여{' '}
            공정코드(JOB_PROCS_CD), 공정명(JOB_PROCS_NM),
            공정설명(JOB_PROCS_DESC), 기계설비 설명(JOB_PROCS_MCHN_EXPLN) 등을
            조회합니다.
            <br />
            <strong>TB_BS_RISK_LIBRARY_API_1</strong> 테이블에 INSERT합니다.
          </Typography>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Button
              variant="contained"
              color="info"
              onClick={SyncAllRiskLibraryOnly}
              disabled={busy}
              sx={{ fontWeight: 'bold' }}
            >
              {syncAllRiskLibraryOnlyLoading
                ? '전 업종 공정 동기화 중...'
                : '전 업종 공정 동기화 (SyncAllRiskLibraryOnly)'}
            </Button>
            {syncAllRiskLibraryOnlyLoading && (
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <LinearProgress variant="indeterminate" color="info" />
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      {/* 3. 공정별 위험발생 상황 및 결과 상세 */}
      <Card
        variant="outlined"
        sx={{ borderLeft: '4px solid', borderColor: 'success.main' }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            3. 공정별 위험발생 상황 및 결과 상세
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            TB_BS_RISK_LIBRARY_API_1 테이블에 있는 공정을 모두 순회하며 공단의{' '}
            <code>selectRiskFactor</code> API와 연동해 위험평가 상세(위험발생
            상황 및 결과, 관련근거(법령), 조문번호, 조문제목, 위험요인,
            안전보건조치 등)를 조회합니다.
            <br />
            <strong>TB_BS_RISK_DETAIL_API</strong> 테이블에 INSERT합니다.
            <br />
          </Typography>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              variant="contained"
              color="warning"
              onClick={SyncAllRiskDetail}
              disabled={busy}
            >
              {syncDetailAllLoading
                ? '상세 일괄 처리 중...'
                : '상세 일괄 (SyncAllRiskDetail)'}
            </Button>
            {syncDetailAllLoading && (
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <LinearProgress variant="indeterminate" />
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      {/* 4. 공단 포털 API 설정 (KoshaPortal) */}
      <Card
        variant="outlined"
        sx={{ borderLeft: '4px solid', borderColor: 'info.main' }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            4. 공단 포털 API 설정 (KoshaPortal)
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            산업안전보건공단 포털 주소:{' '}
            <Link
              href="https://portal.kosha.or.kr/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://portal.kosha.or.kr/
            </Link>
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            아래는 서버에서 산업안전보건공단 포털을 호출할 때 사용하는 경로를
            구현해놓은 서버단 <strong>appsettings.json</strong> 파일의
            KoshaPortal 블록입니다. 이 블록의 Authorization, Cookie 값을 임의로
            입력하여 데이터를 들고오는 형태입니다.
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Authorization</strong>, <strong>Cookie</strong> 값은
            산업안전공단 포털에서 위험성평가를 실시하는 화면과 동일한 세션으로
            맞추기 위해, 브라우저 <strong>개발자 도구(F12)</strong> →{' '}
            <strong>Network</strong> 탭 → 해당 페이지에서 발생한 요청의{' '}
            <strong>Name</strong>을 클릭 → <strong>Headers</strong>에 보이는
            내용을 그대로 복사해 설정에 붙여 넣은 뒤 동기화를 실행해야 합니다.
            <br />
            서버 재시작 필수!!
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            <strong>appsettings.json</strong>
          </Typography>
          <Box component="pre" sx={{ ...codePreSx, maxHeight: 480 }}>
            {KOSHA_PORTAL_CONFIG_HEAD}
            <Box component="span" sx={koshaPortalHighlightSx}>
              {`    "Authorization": "여기에 복붙",
`}
            </Box>
            {KOSHA_PORTAL_CONFIG_REFERER_LINE}
            <Box component="span" sx={koshaPortalHighlightSx}>
              {`    "Cookie": "여기에 복붙"
`}
            </Box>
            {KOSHA_PORTAL_CONFIG_TAIL}
          </Box>
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      {/* 5. 공단 API 응답과 dataList 형식 */}
      <Card
        variant="outlined"
        sx={{ borderLeft: '4px solid', borderColor: 'grey.700' }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            5. 공단 API 응답과 <code>dataList</code> 형식
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            공단 포털 API는 JSON으로 응답하며, 목록 데이터는 보통{' '}
            <code>dataList</code>라는 이름의 <strong>배열</strong>에 담겨
            내려옵니다. 동기화 서버는 이 배열을 순회해 DB 테이블에
            매핑·저장합니다. 아래는 대표 호출 순서와 URL, 그리고{' '}
            <code>dataList</code>에 들어오는 객체 형태 예시입니다.
          </Typography>

          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                * 사전준비(공단 화면 기준)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                위험성평가 실시 전 단계에서 세션·토큰 등을 맞추기 위한
                호출입니다.
              </Typography>
              <Link
                href="https://portal.kosha.or.kr/api/portal24/bizC/CRSBA01002/selectKrasPrpare"
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ wordBreak: 'break-all' }}
              >
                https://portal.kosha.or.kr/api/portal24/bizC/CRSBA01002/selectKrasPrpare
              </Link>
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                * 업종선택 팝업(공단 화면 기준)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                업종 코드, 세분류코드 세부분류, 산재업종-대중소/표준산업-대중소
                목록이 <code>dataList</code> 등으로 내려옵니다.
              </Typography>
              <Link
                href="https://portal.kosha.or.kr/api/portal24/bizC/CRSBA01002/selectIndustryPopList"
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ wordBreak: 'break-all' }}
              >
                https://portal.kosha.or.kr/api/portal24/bizC/CRSBA01002/selectIndustryPopList
              </Link>
              <Box component="pre" sx={codePreSx}>
                {KOSHA_INDUSTRY_DATALIST_EXAMPLE}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                * 업종별 공정 조회
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                선택한 업종에 속한 공정(작업) 목록이 <code>dataList</code>에
                담깁니다. 공정코드·공정명·작업설명·기계설비 설명·순번 등 필드가
                객체 단위로 옵니다.
              </Typography>
              <Link
                href="https://portal.kosha.or.kr/api/portal24/bizC/CRSBA01002/selectPostIaTpbiz"
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ wordBreak: 'break-all', display: 'block', mb: 1 }}
              >
                https://portal.kosha.or.kr/api/portal24/bizC/CRSBA01002/selectPostIaTpbiz
              </Link>
              <Box component="pre" sx={codePreSx}>
                {KOSHA_PROCESS_DATALIST_EXAMPLE}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                * 위험발생 상황 및 결과, 관련근거(법령), 조문번호, 조문제목,
                위험요인, 안전보건조치
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                공정코드(<code>iaTpbizJobProcsCd</code>) 기준으로
                위험요인·법령·조문 본문(<code>sttLprvsCn</code>
                )·세부분류·안전보건조치(
                <code>addActnCn</code>, 줄바꿈은 <code>\r\n</code>) 등이{' '}
                <code>dataList</code> 항목으로 내려옵니다. 조문 본문은 매우 길
                수 있어 실제 응답과 동일하지 않게 축약한 예시입니다.
              </Typography>
              <Link
                href="https://portal.kosha.or.kr/api/portal24/bizC/CRSBA01003/selectRiskFactor"
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ wordBreak: 'break-all', display: 'block', mb: 1 }}
              >
                https://portal.kosha.or.kr/api/portal24/bizC/CRSBA01003/selectRiskFactor
              </Link>
              <Box component="pre" sx={{ ...codePreSx, maxHeight: 420 }}>
                {KOSHA_RISK_FACTOR_DATALIST_EXAMPLE}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default KOSHA_TEMP_01;
